import { formatNumber } from '@/utils/number';
import type { BillingLimits } from '../types/billing.types';

export type BillingMode = 'verify' | 'approve';

export interface Verifiability {
  canDecide: boolean;
  reason: string | undefined;
}

interface QueueRowStates {
  status: string;
  approveStatus: string;
}

const ALREADY: Record<BillingMode, string> = {
  verify: 'Already verified',
  approve: 'Already approved',
};

function blocker(row: QueueRowStates, mode: BillingMode): string | undefined {
  const decided = mode === 'verify' ? row.status : row.approveStatus;
  if (decided === 'accepted') {
    return ALREADY[mode];
  }
  if (mode === 'approve' && row.status !== 'accepted') {
    return 'Not verified yet';
  }
  return undefined;
}

function tooOldReason(ageDays: number, dayLimit: number): string {
  return `${formatNumber(ageDays)} days old (limit ${formatNumber(dayLimit)})`;
}

export function dcrVerifiability(
  row: QueueRowStates & { maxSpecimenQty: number; entryAgeDays: number },
  limits: BillingLimits,
  mode: BillingMode,
): Verifiability {
  const blocked = blocker(row, mode);
  if (blocked) {
    return { canDecide: false, reason: blocked };
  }
  if (row.entryAgeDays > limits.approveDayHigh) {
    return {
      canDecide: false,
      reason: tooOldReason(row.entryAgeDays, limits.approveDayHigh),
    };
  }
  if (row.maxSpecimenQty > limits.specimenQtyHigh) {
    return {
      canDecide: false,
      reason: `${formatNumber(row.maxSpecimenQty)} specimens (limit ${formatNumber(limits.specimenQtyHigh)})`,
    };
  }
  return { canDecide: true, reason: undefined };
}

export function derVerifiability(
  row: QueueRowStates & { entryAgeDays: number },
  limits: BillingLimits,
  mode: BillingMode,
): Verifiability {
  const blocked = blocker(row, mode);
  if (blocked) {
    return { canDecide: false, reason: blocked };
  }
  if (row.entryAgeDays > limits.approveDayHigh) {
    return {
      canDecide: false,
      reason: tooOldReason(row.entryAgeDays, limits.approveDayHigh),
    };
  }
  return { canDecide: true, reason: undefined };
}

export type AmountCapKind = 'claimed' | 'verified';

export interface AmountCap {
  amount: number;
  kind: AmountCapKind;
}

const AMOUNT_CAP_KIND: Record<BillingMode, AmountCapKind> = {
  verify: 'claimed',
  approve: 'verified',
};

export function getAmountCap(mode: BillingMode, claimed: number, verified: number): AmountCap {
  return { amount: mode === 'verify' ? claimed : verified, kind: AMOUNT_CAP_KIND[mode] };
}

export function getStartingTransportTypeId(
  mode: BillingMode,
  row: { transportTypeId: number | null; approveTransportTypeId: number | null },
): number | null {
  if (mode === 'verify') return row.transportTypeId;
  return row.approveTransportTypeId ?? row.transportTypeId;
}

export function amountProblem(
  approved: number | undefined,
  cap: AmountCap,
  limit: { low: number; high: number },
  noun: string,
): string | undefined {
  if (approved === undefined) {
    return `Enter the ${noun}`;
  }
  if (approved > cap.amount) {
    return `Over the ${cap.kind} ${formatNumber(cap.amount)} Tk`;
  }
  if (approved < limit.low) {
    return `Below the ${formatNumber(limit.low)} Tk minimum`;
  }
  if (approved > limit.high) {
    return `Over the ${formatNumber(limit.high)} Tk limit`;
  }
  return undefined;
}
